import { DataProvider } from "@refinedev/core";
import axios from "axios";
import { APP_CONFIG } from "./constants";
import { hasNativeAuthBridge, requestNativeAccessToken } from "./mobileAuthBridge";

const API_URL = APP_CONFIG.API_BASE;
const TOKEN_KEY = APP_CONFIG.STORAGE_KEYS.TOKEN;
const refreshClient = axios.create({ withCredentials: true });
let refreshPromise: Promise<string | null> | null = null;

// Create instance or use global axios with interceptors
export const axiosInstance = axios;
axiosInstance.defaults.withCredentials = true;

axiosInstance.interceptors.request.use((config: any) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

const refreshAccessToken = async (): Promise<string | null> => {
  if (hasNativeAuthBridge()) {
    return requestNativeAccessToken(true);
  }
  if (!refreshPromise) {
    refreshPromise = refreshClient
      .post(`${API_URL}/auth/refresh-token`, null)
      .then((response) => {
        const nextToken = response.data?.token as string | undefined;
        if (!nextToken) {
          throw new Error("Refresh token response is invalid.");
        }

        localStorage.setItem(TOKEN_KEY, nextToken);
        localStorage.removeItem(APP_CONFIG.STORAGE_KEYS.REFRESH);
        window.dispatchEvent(new CustomEvent("auth-state-changed"));
        if (typeof window !== "undefined") {
          (window as any).flutter_inappwebview?.callHandler?.("TOKEN_UPDATED", {
            accessToken: nextToken,
            refreshToken: response.data?.refreshToken
          });
        }
        return nextToken;
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(APP_CONFIG.STORAGE_KEYS.REFRESH);
        window.dispatchEvent(new CustomEvent("auth-state-changed"));
        if (typeof window !== "undefined") {
          (window as any).flutter_inappwebview?.callHandler?.("TOKEN_CLEARED");
        }
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: any) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;
    const requestUrl = String(originalRequest?.url || "");

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !requestUrl.includes("/auth/login") &&
      !requestUrl.includes("/auth/firebase-signin") &&
      !requestUrl.includes("/auth/refresh-token")
    ) {
      originalRequest._retry = true;
      const nextToken = await refreshAccessToken();

      if (nextToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["Authorization"] = `Bearer ${nextToken}`;
        return axiosInstance(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

const getMappedResource = (resource: string): string => {
  const mapping: Record<string, string> = {
    "model": "models",
    "collection": "collections",
    "toybrand": "brands",
    "admin-brands": "admin/brands",
    "admin-badges": "Badges",
    "badges": "Badges",
    "admin-fields": "FieldDefinitions",
    "fields": "FieldDefinitions",
    "admin/fields": "FieldDefinitions",
    "admin-users": "users",
    "users": "users",
    "admin-quest-templates": "admin/quest-templates",
  };

  const lowerResource = resource.toLowerCase();
  if (mapping[lowerResource]) return mapping[lowerResource];

  if (resource.includes("/")) {
    const parts = resource.split("/");
    if (parts[0].toLowerCase() === "admin") {
      return parts.length >= 2 ? parts[1] : parts[0];
    }
    return resource;
  }

  return resource;
};

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters }: any) => {
    let apiResource = resource;
    
    const mapping: Record<string, string> = {
      "model": "models",
      "collection": "collections",
      "toybrand": "brands",
      "admin-brands": "admin/brands",
      "admin-badges": "Badges",
      "admin-fields": "FieldDefinitions",
      "fields": "FieldDefinitions",
      "admin/fields": "FieldDefinitions",
      "admin-users": "users",
      "users": "users",
      "admin-quest-templates": "admin/quest-templates",
    };

    if (mapping[resource.toLowerCase()]) {
        apiResource = mapping[resource.toLowerCase()];
    } else if (resource.includes("/")) {
      const parts = resource.split("/");
      if (parts[0].toLowerCase() === "admin") {
          apiResource = parts.length >= 2 ? parts[1] : parts[0];
      } else {
          // If it's a sub-path like UserMedia/pending-approvals, use it as is
          apiResource = resource;
      }
    }

    const pagedResources = ["users", "models", "collections", "brands", "wishlist", "messages", "model", "collection", "toybrand", "badges"];
    const normalizedResource = apiResource.toLowerCase();

    const isPaged = pagedResources.some(r => normalizedResource.includes(r));

    if (isPaged) {
      const resourceName = apiResource.charAt(0).toUpperCase() + apiResource.slice(1);
      const page = (pagination as any)?.current || 1;
      const pageSize = pagination?.pageSize || 25;

      // Extract search query from filters
      const q = filters?.find((f: any) => f.field === "q" || f.field === "search")?.value;

      const params: any = { page, pageSize, q };
      if (filters && Array.isArray(filters)) {
        filters.forEach((f: any) => {
          if (f.field !== "q" && f.field !== "search") {
            params[f.field] = f.value;
          }
        });
      }

      if (sorters && sorters.length > 0) {
        params.orderBy = sorters[0].field;
        params.isDescending = sorters[0].order === "desc";
      }

      try {
        const response = await axiosInstance.get(`${API_URL}/${resourceName}`, {
          params,
        });
        const resData = response.data;
        return {
          data: resData.data ?? resData,
          total: resData.totalCount ?? resData.total ?? (Array.isArray(resData) ? resData.length : 0),
        };
      } catch (error) {
        console.error(`Error fetching ${resourceName}:`, error);
        throw error;
      }
    }

    const response = await axiosInstance.get(`${API_URL}/${apiResource}`);
    return {
      data: Array.isArray(response.data) ? response.data : [response.data],
      total: Array.isArray(response.data) ? response.data.length : 1,
    };
  },

  getOne: async ({ resource, id }: any) => {
    const apiResource = getMappedResource(resource);
    const resourceName = apiResource.charAt(0).toUpperCase() + apiResource.slice(1);
    const url = `${API_URL}/${resourceName}/${id}`;
    const response = await axiosInstance.get(url);
    return { data: response.data?.data ?? response.data };
  },

  create: async ({ resource, variables }: any) => {
    const apiResource = getMappedResource(resource);
    const resourceName = apiResource.charAt(0).toUpperCase() + apiResource.slice(1);
    const url = `${API_URL}/${resourceName}`;
    const response = await axiosInstance.post(url, variables);
    return { data: response.data?.data ?? response.data };
  },

  update: async ({ resource, id, variables }: any) => {
    const apiResource = getMappedResource(resource);
    const resourceName = apiResource.charAt(0).toUpperCase() + apiResource.slice(1);
    const url = `${API_URL}/${resourceName}/${id}`;
    const response = await axiosInstance.put(url, variables);
    return { data: response.data?.data ?? response.data };
  },

  deleteOne: async ({ resource, id }: any) => {
    const apiResource = getMappedResource(resource);
    const resourceName = apiResource.charAt(0).toUpperCase() + apiResource.slice(1);
    const url = `${API_URL}/${resourceName}/${id}`;
    const response = await axiosInstance.delete(url);
    return { data: response.data?.data ?? response.data };
  },

  custom: async ({ url, method, payload, query, headers }: any) => {
    let cleanUrl = url.startsWith("/") ? url : `/${url}`;
    // Prevent double /api
    if (cleanUrl.startsWith("/api/")) {
      cleanUrl = cleanUrl.replace("/api/", "/");
    }

    const fullUrl = url.startsWith("http")
      ? url
      : `${APP_CONFIG.API_BASE}${cleanUrl}`;

    const config = {
      headers: headers || {},
      params: query
    };

    let response;
    switch (method.toLowerCase()) {
      case "post":
        response = await axiosInstance.post(fullUrl, payload, config);
        break;
      case "put":
        response = await axiosInstance.put(fullUrl, payload, config);
        break;
      case "patch":
        response = await axiosInstance.patch(fullUrl, payload, config);
        break;
      case "delete":
        response = await axiosInstance.delete(fullUrl, { ...config, data: payload });
        break;
      default:
        response = await axiosInstance.get(fullUrl, config);
        break;
    }
    return { data: response.data };
  },

  getApiUrl: () => API_URL,
};
